// frontend/src/pages/asset-admin/AssetAdminFleetOffences.js
/**
 * Asset Admin — Fleet Traffic Offences
 *
 * The custodian's working queue for traffic offence notices, ordered by
 * deadline rather than by vehicle. Notices are mirrored from the Ops Portal
 * (system-of-record for the notice itself). HR owns who-was-driving and the
 * declaration-deadline chase.
 *
 * Two clocks run on every notice and they have different consequences:
 *
 *   - Declaration deadline — failing to furnish driver particulars in time is
 *     a separate offence under the Road Traffic Act, so it governs the queue
 *     until particulars are furnished.
 *   - Payment deadline — an unpaid fine escalates to a court summons and a
 *     Warrant of Arrest, which blocks road tax and work pass renewals.
 *
 * Per-vehicle history lives in AssetDetailModal → Traffic Offences tab.
 *
 * Data: GET /asset-inventory/traffic-offences/ (+ /summary/, /sync-status/)
 * Theme: Red (Admin).
 */

import React, { useMemo, useState } from 'react';
import {
  ShieldAlert, AlertTriangle, CalendarClock, CircleDollarSign, RefreshCw,
  ChevronRight, User, AlertCircle, Search,
} from 'lucide-react';
import PillToggle from '../../components/UI/PillToggle';
import { StatusBadge } from '../../components/UI/StatusBadge';
import { CountBadge } from '../../components/UI/CountBadge';
import DebouncedSearchInput from '../../components/UI/DebouncedSearchInput';
import TrafficOffenceDetailModal from '../../components/asset/TrafficOffenceDetailModal';
import SetDeclarationDeadlineModal from '../../components/asset/SetDeclarationDeadlineModal';
import ChangeOffenceDriverModal from '../../components/asset/ChangeOffenceDriverModal';
import { formatDate, formatDateTime, formatDaySpan } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/numberUtils';
import {
  useTrafficOffences,
  useTrafficOffenceSummary,
  useTrafficOffenceSyncStatus,
  useSyncTrafficOffences,
} from '../../hooks/useAssetInventoryData';
import { useAssetDetail } from '../../contexts/AssetDetailContext';

const PRE_DECLARATION = ['pending_driver_confirmation', 'disputed', 'pending_declaration'];

const badgeStatusFor = (status) => (status === 'closed' ? 'offence_closed' : status);

const countPhrase = (n, singular, plural) => `${n} ${n === 1 ? singular : plural}`;

const METRIC_TONES = {
  red: 'bg-red-100 text-red-600',
  amber: 'bg-amber-100 text-amber-600',
  blue: 'bg-blue-100 text-blue-600',
  purple: 'bg-purple-100 text-purple-600',
};

const MetricCard = ({ title, value, subtitle, icon: Icon, color = 'red' }) => (
  <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
    {/* Icon stacks above the value on the 2-up mobile grid so a dollar
        figure is not truncated beside the circle. Inline from sm.
        items-start keeps the icon paired with the hero number when a
        subtitle adds a third line. */}
    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-0">
      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${METRIC_TONES[color]}`}>
        <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
      </div>
      <div className="sm:ml-4 min-w-0">
        <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{value}</p>
        <p className="text-xs sm:text-sm text-gray-600 truncate">{title}</p>
        {subtitle && (
          <p className="text-[11px] sm:text-xs text-gray-500 mt-1 truncate">{subtitle}</p>
        )}
      </div>
    </div>
  </div>
);

const PageSkeleton = () => (
  <div className="space-y-6">
    <div className="flex items-center justify-between gap-3 animate-pulse">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-gray-200 rounded-full flex-shrink-0" />
          <div className="h-7 sm:h-8 bg-gray-200 rounded w-48" />
        </div>
        <div className="h-4 bg-gray-200 rounded w-72 ml-11 sm:ml-12" />
      </div>
      <div className="w-11 h-11 bg-gray-200 rounded-full flex-shrink-0" />
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 animate-pulse">
          <div className="flex flex-col sm:flex-row sm:items-start gap-2">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex-shrink-0" />
            <div className="space-y-2 sm:ml-2 min-w-0 flex-1">
              <div className="h-7 bg-gray-200 rounded w-16" />
              <div className="h-4 bg-gray-200 rounded w-24" />
            </div>
          </div>
        </div>
      ))}
    </div>
    <div className="bg-white rounded-2xl shadow-sm border h-96 animate-pulse" />
  </div>
);

/**
 * The deadline that currently matters. Declaration governs until particulars
 * are furnished — including when the date has not been set yet. Payment
 * takes over only after that, or immediately for a company offence.
 */
const activeDeadline = (notice) => {
  const preDeclaration = PRE_DECLARATION.includes(notice.status);
  if (preDeclaration && !notice.is_company_offence && !notice.declaration_deadline) {
    return { kind: 'declare', missing: true };
  }
  if (preDeclaration && notice.declaration_deadline) {
    return {
      kind: 'declare',
      date: notice.declaration_deadline,
      days: notice.days_to_declaration_deadline,
    };
  }
  if (notice.payment_deadline) {
    return {
      kind: 'pay',
      date: notice.payment_deadline,
      days: notice.days_to_payment_deadline,
    };
  }
  return null;
};

const queueRank = (notice) => {
  const deadline = activeDeadline(notice);
  if (!deadline) return 4;
  if (deadline.missing) return 0;
  const days = Number(deadline.days);
  if (!Number.isFinite(days)) return 3;
  if (days <= 0) return 1;
  if (days <= 3) return 2;
  return 3;
};

const urgencyText = (days) => {
  if (days === 0) return 'Due today';
  if (days < 0) return `${formatDaySpan(Math.abs(days))} overdue`;
  return `${formatDaySpan(days)} left`;
};

const DeadlineCell = ({ notice }) => {
  const deadline = activeDeadline(notice);
  if (!deadline) return <span className="text-sm text-gray-500">—</span>;
  if (deadline.missing) {
    return (
      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset whitespace-nowrap bg-amber-100 text-amber-700 ring-amber-600/20">
        Deadline needed
      </span>
    );
  }

  const days = Number(deadline.days);
  const known = Number.isFinite(days);
  const tone = !known
    ? 'bg-gray-100 text-gray-700 ring-gray-500/20'
    : days <= 0
      ? 'bg-red-100 text-red-700 ring-red-600/20'
      : days <= 3
        ? 'bg-amber-100 text-amber-700 ring-amber-600/20'
        : 'bg-gray-100 text-gray-700 ring-gray-500/20';
  const clock = deadline.kind === 'declare' ? 'Declare' : 'Pay';

  return (
    <div className="min-w-0">
      <div className="text-sm text-gray-900">{formatDate(deadline.date)}</div>
      {known && (
        <span
          className={`mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset whitespace-nowrap ${tone}`}
          title={`${clock} by ${formatDate(deadline.date)}`}
        >
          {clock}: {urgencyText(days)}
        </span>
      )}
    </div>
  );
};

/** What the custodian actually has to do next, in plain words. */
const nextActionFor = (notice) => {
  switch (notice.status) {
    case 'pending_driver_confirmation':
      return notice.current_driver_name
        ? `Waiting on ${notice.current_driver_name}`
        : 'Select a driver';
    case 'disputed':
      return 'Resolve dispute — find the real driver';
    case 'pending_declaration':
      if (!notice.declaration_deadline) return 'Set declaration deadline';
      return 'Furnish particulars to the authority';
    case 'pending_payment':
      return notice.has_payment_proof ? 'Verify the receipt' : 'Waiting on payment';
    default:
      return '';
  }
};

const formatSyncedAt = (iso) => {
  if (!iso) return 'Never synced';
  try {
    return `Last synced ${formatDateTime(iso)}`;
  } catch {
    return 'Last synced —';
  }
};

const openNotice = (setDetailId, id) => () => setDetailId(id);

const onNoticeKeyDown = (setDetailId, id) => (event) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    setDetailId(id);
  }
};

const AssetAdminFleetOffences = () => {
  const [scope, setScope] = useState('open');
  const [search, setSearch] = useState('');
  const [detailId, setDetailId] = useState(null);
  const [deadlineNotice, setDeadlineNotice] = useState(null);
  const [changingDriver, setChangingDriver] = useState(null);

  const params = {
    ...(scope === 'all' ? { include_closed: 'true' } : {}),
    ...(search ? { search } : {}),
  };
  const { data: notices = [], isLoading, error, refetch } = useTrafficOffences(params);
  const { data: summary } = useTrafficOffenceSummary();
  const { data: syncStatus } = useTrafficOffenceSyncStatus();
  const syncMutation = useSyncTrafficOffences();
  const { openAssetDetail } = useAssetDetail();

  const orderedNotices = useMemo(() => {
    return notices
      .map((notice, index) => ({ notice, index }))
      .sort((a, b) => {
        const rankDelta = queueRank(a.notice) - queueRank(b.notice);
        if (rankDelta !== 0) return rankDelta;
        const daysA = Number(activeDeadline(a.notice)?.days);
        const daysB = Number(activeDeadline(b.notice)?.days);
        const safeA = Number.isFinite(daysA) ? daysA : Number.POSITIVE_INFINITY;
        const safeB = Number.isFinite(daysB) ? daysB : Number.POSITIVE_INFINITY;
        if (safeA !== safeB) return safeA - safeB;
        return a.index - b.index;
      })
      .map(({ notice }) => notice);
  }, [notices]);

  const driftMessages = useMemo(() => {
    const d = syncStatus?.drift || {};
    const msgs = [];
    if (d.missing_declaration_deadline > 0) {
      msgs.push(
        `${countPhrase(d.missing_declaration_deadline, 'notice needs', 'notices need')} a declaration deadline`
      );
    }
    if (d.ops_ack_but_awaiting_driver > 0) {
      msgs.push(
        `${countPhrase(d.ops_ack_but_awaiting_driver, 'notice', 'notices')} acknowledged in Ops, still waiting on the driver`
      );
    }
    if (d.ops_paid_but_open > 0) {
      msgs.push(
        `${countPhrase(d.ops_paid_but_open, 'notice', 'notices')} marked paid in Ops, still open here`
      );
    }
    if (d.hr_closed_ops_pending > 0) {
      msgs.push(
        `${countPhrase(d.hr_closed_ops_pending, 'closed notice is', 'closed notices are')} still pending acknowledgement in Ops`
      );
    }
    if (d.push_backlog > 0) {
      msgs.push(
        `${countPhrase(d.push_backlog, 'update is', 'updates are')} still waiting to push to Ops`
      );
    }
    return msgs;
  }, [syncStatus]);

  if (isLoading) return <PageSkeleton />;

  const awaitingDriver = (summary?.awaiting_driver ?? 0) + (summary?.disputed ?? 0);
  const disputed = summary?.disputed ?? 0;
  const awaitingPayment = summary?.awaiting_payment ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 mb-1 min-w-0">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <ShieldAlert className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
            </div>
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">
              Traffic Offences
            </h1>
          </div>
          <p className="text-xs sm:text-sm text-gray-500 ml-11 sm:ml-12 truncate">
            Notices from Ops Portal. Confirm the driver, then chase the declaration.
          </p>
        </div>
        <div className="flex items-center gap-2 min-w-0 self-start sm:self-auto sm:flex-shrink-0 ml-11 sm:ml-0">
          <span
            className="text-xs text-gray-500 truncate"
            title={formatSyncedAt(syncStatus?.last_synced_at)}
          >
            {formatSyncedAt(syncStatus?.last_synced_at)}
          </span>
          <button
            type="button"
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
            className="icon-button-admin flex-shrink-0"
            title="Sync from Ops Portal"
            aria-label="Sync from Ops Portal"
          >
            <RefreshCw className={`w-4 h-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {driftMessages.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-900">Attention needed</p>
            </div>
          </div>
          <ul className="mt-3 list-disc list-inside text-sm text-amber-900 space-y-0.5">
            {driftMessages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <MetricCard
          title="Overdue"
          value={summary?.overdue ?? 0}
          subtitle="Deadline already passed"
          icon={AlertTriangle}
          color="red"
        />
        <MetricCard
          title="Due this week"
          value={summary?.due_this_week ?? 0}
          subtitle="Next 7 days"
          icon={CalendarClock}
          color="amber"
        />
        <MetricCard
          title="Awaiting driver"
          value={awaitingDriver}
          subtitle={countPhrase(disputed, 'disputed', 'disputed')}
          icon={User}
          color="blue"
        />
        <MetricCard
          title="Unpaid fines"
          value={formatCurrency(summary?.unpaid_amount || 0, { grouped: true })}
          subtitle={countPhrase(awaitingPayment, 'notice', 'notices')}
          icon={CircleDollarSign}
          color="purple"
        />
      </div>

      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center min-w-0 gap-2">
            <span className="truncate">{scope === 'open' ? 'Open notices' : 'All notices'}</span>
            <CountBadge
              count={orderedNotices.length}
              theme="admin"
              size="md"
              max={9999}
              className="flex-shrink-0"
            />
          </h2>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full lg:w-auto lg:flex-shrink-0">
            <DebouncedSearchInput
              value={search}
              onSearch={setSearch}
              placeholder="Search notice or plate..."
              theme="admin"
              className="w-full sm:max-w-xs sm:flex-none"
            />
            <PillToggle
              value={scope}
              onChange={setScope}
              options={[
                { id: 'open', label: 'Open' },
                { id: 'all', label: 'All' },
              ]}
              theme="admin"
              layout="sectionHeader"
              ariaLabel="Notice scope"
            />
          </div>
        </div>

        {error && orderedNotices.length === 0 ? (
          <div className="text-center py-12 px-4">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Could not load traffic offences</h3>
            <p className="text-sm text-gray-500">The queue did not load. Try again, or sync from Ops Portal.</p>
            <button type="button" onClick={() => refetch()} className="btn btn-admin-primary mt-4">
              <RefreshCw className="w-4 h-4" />
              Try again
            </button>
          </div>
        ) : orderedNotices.length === 0 ? (
          <div className="text-center py-12 px-4">
            {search ? (
              <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            ) : (
              <ShieldAlert className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            )}
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {search ? 'No matching notices' : scope === 'open' ? 'No open notices' : 'No notices'}
            </h3>
            <p className="text-sm text-gray-500">
              {search
                ? 'Try a different notice number or plate.'
                : scope === 'open'
                  ? 'Sync from Ops to pull new notices, or switch to All to see closed ones.'
                  : 'Sync from Ops to pull notices.'}
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-3 p-4 md:hidden">
              {orderedNotices.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={openNotice(setDetailId, n.id)}
                  className="w-full text-left p-4 rounded-xl border border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-red-500"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {n.vehicle_number_raw}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {n.notice_number} · {n.offence_type_display}
                      </p>
                    </div>
                    <StatusBadge status={badgeStatusFor(n.status)} />
                  </div>
                  <div className="flex items-end justify-between gap-2">
                    <DeadlineCell notice={n} />
                    <span className="text-sm tabular-nums text-gray-900 flex-shrink-0">
                      {formatCurrency(n.fine_amount || 0, { grouped: true })}
                    </span>
                  </div>
                  {nextActionFor(n) && (
                    <p className="text-xs text-gray-500 mt-2">{nextActionFor(n)}</p>
                  )}
                </button>
              ))}
            </div>

            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Vehicle / notice</th>
                    <th className="table-header-cell">Offence</th>
                    <th className="table-header-cell">Driver</th>
                    <th className="table-header-cell">Deadline</th>
                    <th className="table-header-cell text-center">Status</th>
                    <th className="table-header-cell text-right">Fine</th>
                    <th className="table-header-cell text-right">
                      <span className="sr-only">Open</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {orderedNotices.map((n) => (
                    <tr
                      key={n.id}
                      tabIndex={0}
                      onClick={openNotice(setDetailId, n.id)}
                      onKeyDown={onNoticeKeyDown(setDetailId, n.id)}
                      aria-label={`Open notice ${n.notice_number}`}
                      className="table-row cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-red-500"
                    >
                      <td className="table-cell whitespace-normal">
                        <div className="font-semibold text-gray-900">{n.vehicle_number_raw}</div>
                        <div className="text-xs text-gray-500">{n.notice_number}</div>
                      </td>
                      <td className="table-cell whitespace-normal">
                        <div className="text-gray-900">{n.offence_type_display}</div>
                        <div className="text-xs text-gray-500">{formatDateTime(n.offence_datetime)}</div>
                      </td>
                      <td className="table-cell whitespace-normal">
                        {n.is_company_offence ? (
                          <span className="text-sm text-gray-500">Company offence</span>
                        ) : (
                          <>
                            <div className="text-gray-900">{n.current_driver_name || '—'}</div>
                            {nextActionFor(n) && (
                              <div className="text-xs text-gray-500">{nextActionFor(n)}</div>
                            )}
                          </>
                        )}
                      </td>
                      <td className="table-cell whitespace-normal">
                        <DeadlineCell notice={n} />
                      </td>
                      <td className="table-cell text-center">
                        <StatusBadge status={badgeStatusFor(n.status)} />
                      </td>
                      <td className="table-cell text-right tabular-nums">
                        {formatCurrency(n.fine_amount || 0, { grouped: true })}
                      </td>
                      <td className="table-cell text-right">
                        <ChevronRight className="w-4 h-4 text-gray-400 inline" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <TrafficOffenceDetailModal
        isOpen={!!detailId}
        onClose={() => setDetailId(null)}
        noticeId={detailId}
        onViewVehicle={(assetId) => {
          setDetailId(null);
          openAssetDetail(assetId);
        }}
        onChangeDriver={(notice) => setChangingDriver(notice)}
        onSetDeadline={(notice) => setDeadlineNotice(notice)}
      />

      <SetDeclarationDeadlineModal
        isOpen={!!deadlineNotice}
        onClose={() => setDeadlineNotice(null)}
        notice={deadlineNotice}
      />

      <ChangeOffenceDriverModal
        isOpen={!!changingDriver}
        onClose={() => setChangingDriver(null)}
        notice={changingDriver}
      />
    </div>
  );
};

export default AssetAdminFleetOffences;
